import { removeDiacritics } from './normalize'

import { I18n } from '@/i18n/I18n'
import { TranslationTokens, VarsByToken } from '@/i18n/ITranslations'

/**
 * Format bytes into human readable string.
 *
 * @param bytes total of Bytes
 * @returns format Bytes to a string with properly size
 */
export const formatBytes = (bytes: number): string => {
  if (!bytes && bytes !== 0) return null
  if (bytes < 1024) return `${bytes}B`
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + 'KB'
  else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + 'MB'
  return (bytes / 1073741824).toFixed(1) + 'GB'
}

/**
 * Format bytes into a pt-BR human readable string with a space before the unit
 * (e.g. "2,1 MB"). Used in the "Meus Conteúdos" list descriptions. Returns null
 * when there is no size (charts imported before the field existed), so the caller
 * can omit that part of the description.
 */
export const formatFileSizePtBR = (bytes: number): string | null => {
  if (!bytes && bytes !== 0) return null

  const format = (value: number, decimals: number): string =>
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value)

  if (bytes < 1024) return `${format(bytes, 0)} B`
  if (bytes < 1048576) return `${format(bytes / 1024, 1)} KB`
  if (bytes < 1073741824) return `${format(bytes / 1048576, 1)} MB`
  return `${format(bytes / 1073741824, 1)} GB`
}

/**
 * Strips the last extension from a file name (e.g. "op_fazenda.pdf" → "op_fazenda").
 * Returns the name unchanged when there is no extension. Used for the file-item
 * title in the import flows (title = base name; description = full file name).
 */
export const stripFileExtension = (fileName: string): string => {
  if (!fileName) return ''
  const dotIndex = fileName.lastIndexOf('.')
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName
}

export const formatTime = (
  seconds: number,
  t: <T extends TranslationTokens>(...args: VarsByToken[T] extends never ? [T] : [T, VarsByToken[T]]) => string
) => {
  if (seconds >= 60) {
    return `${Math.floor(seconds / 60)} ${
      Math.floor(seconds / 60) > 1 ? t('TIME-UNITS_MINUTES') : t('TIME-UNITS_MINUTE')
    }`
  }
  return `${Math.floor(seconds)} ${Math.floor(seconds) > 1 ? t('TIME-UNITS_SECONDS') : t('TIME-UNITS_SECOND')}`
}

export function removeAccents(str: string) {
  return str?.normalize?.('NFD')?.replace?.(/[\u0300-\u036f]/g, '') ?? ''
}

export function formatPhoneNumber(numero: string) {
  if (!numero) return ''
  const length = numero.length

  if (length === 10) {
    return `(${numero.substring(0, 2)}) ${numero.substring(2, 6)}-${numero.substring(6, 10)}`
  } else {
    return `(${numero.substring(0, 2)}) ${numero.substring(2, 7)}-${numero.substring(7, 11)}`
  }
}

export const phoneMask = (phone: string): string => {
  if (phone.length > 15) phone = phone.substring(0, 15)
  phone = phone.replace(/\D/g, '') // Remove tudo o que não é dígito
  phone = phone.replace(/^(\d{2})(\d)/g, '($1) $2') // Coloca parênteses em volta dos dois primeiros dígitos
  phone = phone.replace(/(\d)(\d{4})$/, '$1-$2') // Coloca hífen entre o quarto e o quinto dígitos
  return phone
}

// Limit a string to a certain length and add an ellipsis if it's longer
export const limitString = (str: string, limit: number): string => {
  if (str.length > limit) {
    return str.substring(0, limit) + '...'
  }

  return str
}

export function normalizeStringSearch(string: string) {
  return removeDiacritics(string)
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export const normalizeForSearch = (str: string) => {
  return removeDiacritics(str)
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

export function numberToFixedLength(number: string | number, len: number, character: string = ' ') {
  let text = number.toString().substring(0, len)

  while (text.length < len) {
    text = `${character}${text}`
  }

  return text
}
